<template>
  <panel-item :class="`conditional_${fieldName}`">
    <row>
      <slot name="icon"></slot>
      <column>
        <!-- NOTE: the header slot is already wrapped in a <label> -->
        <label>
          <slot name="header"></slot>
          <span class="sr-only"><slot name="header"></slot> Conditional</span>
        </label>
      </column>
      <suffix>
        <div class="dropdown">
          <button
            v-if="canAddRule"
            class="dropdown-toggle btn-icon grey-to-blue add-rule"
            :id="`conditionButton_${fieldName}`"
            :disabled="disabled"
            @click="createConditionalDisplayRule"
            v-tooltip="'Add Rule'">
            <span class="sr-only">Add Rule Dropdown</span>
            <span class="icon-text plus"></span>
          </button>
        </div>
      </suffix>
    </row>

    <ul>
      <template v-if="Object.keys(conditionalRuleSets).length > 0">
        <template v-for="(ruleSet, index) in orderedConditionalRuleSets">
          <li>
            <conditional-display-rule
              :key="ruleSet.client_id"
              :class="{ 'condition-rule': true }"
              :conditional-rule-set="ruleSet"
              :totalRuleSets="Object.keys(conditionalRuleSets).length"
              :disabled="disabled"
              :validation-model="validationModel"
              :validation-field-name="validationFieldName"
              v-on:move-up="moveUp"
              v-on:move-down="moveDown"
              v-on:edit:conditional-rule-set="editConditionalRuleSet"
              v-on:remove:conditional-rule-set="removeConditionalRuleSet">
            </conditional-display-rule>
            <slot name="value" :uniqueId="index" :model="ruleSet" fieldName="conditional_value"></slot>
          </li>
        </template>
      </template>

      <li v-if="hasOtherwiseValue">
        <h6 v-if="Object.keys(conditionalRuleSets).length > 0">Otherwise</h6>
        <slot name="value" uniqueId="" :fieldName="fieldName" :model="parent"></slot>
      </li>
      <slot v-else-if="canAddRule" name="default"></slot>
    </ul>

    <conditional-display-modal v-if="editedRuleSet" :parent="parent" :ruleSet="editedRuleSet"
                               :state="modalStateFor(vueModalID.conditionalDisplay)"
                               :index="vueModalID.conditionalDisplay"
                               :key="editedRuleSet.client_id"
                               :conditional-rule-options="conditionalRuleOptions"
                               :configuring-conditional-step="configuringConditionalStep"
                               @toggle-modal="hideConditionalDisplayModal"
                               @finishedSettingCondition="finishedSettingCondition"
                               @cancel="$emit('cancel')"
    >
      <template #edit-rule-set="slotProps">
        <slot name="edit-rule-set" v-bind="slotProps" />
      </template>
    </conditional-display-modal>
  </panel-item>
</template>

<script lang="ts">
  import Vue from "vue";
  import ConditionalDisplayModal from './conditionalDisplayModal.vue';
  import ConditionalRuleSetFactory from "../../models/conditionalRules/conditionalRuleSetFactory"
  import ConditionalDisplayRule from "./conditionalDisplayRule.vue";
  import ConditionalRuleSet from "../../models/conditionalRules/conditionalRuleSet";
  import Guid from "../../helpers/guid";
  import {teleport} from '~/modules/nodeManager';
  import IConditionalRuleContainer from "../../models/conditionalRules/conditionalRuleContainer";
  import {ConditionalFieldDefinition} from "../../models/conditionalRules/conditionalFieldDefinition";
  import {IConditionalRuleOptions} from "~/interfaces/modeling/conditionalRuleInterfaces";
  import IRepo from "../../interfaces/repo";

  export default Vue.extend({
    props: {
      fieldDefinition: Object as () => ConditionalFieldDefinition,
      parent: Object as () => IConditionalRuleContainer,
      ruleSetRepo: Object as () => IRepo<ConditionalRuleSet>,
      disabled: Boolean,
      conditionalRuleOptions: {
        type: Object as () => IConditionalRuleOptions,
        default: () => {}
      },
      hasOtherwiseValue: {
        type: Boolean,
        default: true,
      },
      canAddRule: {
        type: Boolean,
        default: true
      },
      configuringConditionalStep: {
        type: Boolean,
        default: false
      },
      /**
       * with configuringConditionalStep on, the modal will not close automatically
       * this forces it to close anyway
       */
      hideOnSave: {
        type: Boolean,
        default: false
      },
      validationModel: {
        type: Object,
        required: false,
      },
      validationFieldName: {
        type: String,
        required: false,
      },
    },
    mixins: [],
    components: {
      ConditionalDisplayRule,
      ConditionalDisplayModal,
    },
    data: function () {
      return {
        teleport: teleport,
        editedRuleSet: null,
      };
    },
    methods: {
      createConditionalDisplayRule(): void {
        this.editedRuleSet = ConditionalRuleSetFactory.createForContainer(this.parent, this.fieldDefinition, {}, false, true);
        this.$emit('add:conditional-rule-set')
        this.showModal(this.vueModalID.conditionalDisplay);
      },
      editConditionalRuleSet(conditionalRuleSet): void {
        this.editedRuleSet = conditionalRuleSet;
        this.editedRuleSet.triggerChanges = false;
        this.$emit('edit:conditional-rule-set')
        this.showModal(this.vueModalID.conditionalDisplay);
      },
      removeConditionalRuleSet(conditionalRuleSet): void {
        let i = 0;
        for (let ruleSet of this.orderedConditionalRuleSets) {
          ruleSet.sort_order = i++;
        }
        this.$emit('remove:conditional-rule-set')
      },
      hideConditionalDisplayModal(): void {
        this.hideModal(this.vueModalID.conditionalDisplay);
      },
      moveUp(conditionalRuleSet: ConditionalRuleSet) {
        let desiredSortOrder = conditionalRuleSet.sort_order - 1;
        for (let ruleSet of this.orderedConditionalRuleSets) {
          if (ruleSet.sort_order  === desiredSortOrder) {
            ruleSet.sort_order++;
            break;
          }
        }
        conditionalRuleSet.sort_order--;
      },
      moveDown(conditionalRuleSet: ConditionalRuleSet) {
        let desiredSortOrder = conditionalRuleSet.sort_order + 1;
        for (let ruleSet of this.orderedConditionalRuleSets) {
          if (ruleSet.sort_order === desiredSortOrder) {
            ruleSet.sort_order--;
            break;
          }
        }
        conditionalRuleSet.sort_order++;
      },
      finishedSettingCondition(event): void {
        this.$emit('finishedSettingCondition', event);
        if (this.hideOnSave) this.hideConditionalDisplayModal()
      }
    },
    computed: {
      fieldName(): string {
        return this.fieldDefinition.fieldName;
      },
      conditionalRuleSets() {
        return this.parent.getConditionalRuleSets(this.fieldName);
      },
      orderedConditionalRuleSets() {
        return this.parent.getSortedConditionalRuleSets(this.fieldName);
      },
    },
    watch: {
    }
  });
</script>
